/**
 * advance-ai-gates.ts — v4M8.6a AI Warp Gate auto-connect scoring.
 *
 * Pure helpers consumed by v4M8.6b's dispatch wiring. Encodes the
 * pair-scoring heuristic locked at `docs/AI.md §5.6`:
 *
 *   pairScore(A, B) = (1 / galaxyDistance(A, B))
 *                     × (economicValue(A) + economicValue(B))
 *                     × archetypeGateAffinity
 *
 * Plus the Brutal `strategicGateBuildingEnabled` bonus for pairs
 * connecting the empire's homeworld to a forward-operating base
 * (any non-homeworld owned system) — enables rapid reinforcement
 * of attack staging per §3.5.
 *
 * `archetypeGateAffinity` already lives on `EffectiveBehaviour`
 * (composed by v4M8.1; Industrialist 1.5 / Defender 1.2 / Phantom
 * 1.0 / Expander 1.0 / Researcher 0.9 / Predator 0.7). Brutal flag
 * also already there as `strategicGateBuildingEnabled`.
 *
 * v4M8.6a strict-parity — helpers exported + tested, no consumer
 * reads them yet. v4M8.6b wires `pickBestGateCandidatePair` into
 * `dispatchAiGateBuilds` in `advance-ai.ts`.
 */

import { perTickIncomeForPlanet } from './balance-structure-effects';
import type {
  EffectiveBehaviour,
  Empire,
  GameState,
  SystemId,
} from '../state/types/index';

/** Per-tick economic income summed across owned planets in `sysId`. */
export function economicValueForSystem(
  state: GameState,
  empire: Empire,
  sysId: SystemId,
): number {
  const sys = state.systems.get(sysId);
  if (!sys) return 0;
  const popOutput = state.balanceData.economy.popOutputPerTick;
  // Sum of per-pop output rates across the four primary resources —
  // a simple proxy for "income per pop per tick" before per-planet
  // multipliers and class yield. Used as the AI scoring weight only;
  // doesn't need to match the actual income formula exactly.
  const popRate =
    popOutput.credits + popOutput.research + popOutput.minerals + popOutput.fuel;
  let total = 0;
  for (const pid of sys.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    if (planet.ownerEmpireId !== empire.id) continue;
    // Population-driven baseline (no multiplier — the AI doesn't need
    // the precise economy figure, just a relative ranking).
    total += planet.currentPopulation * popRate;
    // Booster perTickIncome contributions (MFix38) per resource axis.
    total += perTickIncomeForPlanet(state, planet, 'credits');
    total += perTickIncomeForPlanet(state, planet, 'research');
    total += perTickIncomeForPlanet(state, planet, 'minerals');
    total += perTickIncomeForPlanet(state, planet, 'fuel');
  }
  return total;
}

/**
 * Brutal homeworld-FOB pair bonus multiplier. Locked at 1.5× per
 * `AI.md §5.6` design intent ("rapid reinforcement of attack
 * staging"). Applied when `strategicGateBuildingEnabled` is true AND
 * exactly one of {aId, bId} is the empire's homeworld system.
 */
export const BRUTAL_HOMEWORLD_FOB_BONUS = 1.5;

/**
 * Score a candidate gate pair (sysA, sysB) for empire under the
 * composed `behaviour`. Higher = more attractive. Returns 0 when:
 *   - the two systems are the same (degenerate pair)
 *   - either system is missing from state
 *   - distance collapses to 0 (overlapping systems — defensive)
 */
export function pairScoreForGateCandidate(
  state: GameState,
  empire: Empire,
  behaviour: EffectiveBehaviour,
  aId: SystemId,
  bId: SystemId,
): number {
  if (aId === bId) return 0;
  const a = state.systems.get(aId);
  const b = state.systems.get(bId);
  if (!a || !b) return 0;
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= 0) return 0;
  const econA = economicValueForSystem(state, empire, aId);
  const econB = economicValueForSystem(state, empire, bId);
  let score = (1 / dist) * (econA + econB) * behaviour.archetypeGateAffinity;

  // Brutal strategic positioning bonus per AI.md §5.6 + §3.5: the
  // homeworld ↔ FOB pair is more valuable because it enables rapid
  // reinforcement of attack staging. Only fires on Brutal empires
  // (the flag is propagated by composeEffectiveBehaviour from the
  // difficulty row). Symmetric — either A or B being the homeworld
  // counts, but a pair of homeworld↔homeworld (impossible by
  // definition, only one homeworld per empire) wouldn't.
  if (behaviour.strategicGateBuildingEnabled) {
    const homeworldSysId = empireHomeworldSystemId(state, empire);
    if (homeworldSysId !== null) {
      const aIsHome = aId === homeworldSysId;
      const bIsHome = bId === homeworldSysId;
      if (aIsHome !== bIsHome) {
        score *= BRUTAL_HOMEWORLD_FOB_BONUS;
      }
    }
  }
  return score;
}

/**
 * Pick the highest-scoring gate-build candidate pair among the
 * empire's owned systems. Returns null when:
 *   - empire owns fewer than 2 systems (no pair possible)
 *   - no pair scores > 0 (e.g. zero economic value across the
 *     candidate set)
 *
 * O(N²) over owned-system count. At v1 scales (typically 3-10 owned
 * systems per empire) this is fine; if empires grow to dozens of
 * systems, swap to a top-K heap.
 */
export function pickBestGateCandidatePair(
  state: GameState,
  empire: Empire,
  behaviour: EffectiveBehaviour,
): { aId: SystemId; bId: SystemId; score: number } | null {
  // Collect unique owned system ids (an empire can own multiple
  // planets in the same system — dedup by Set).
  const owned = new Set<SystemId>();
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (planet) owned.add(planet.systemId);
  }
  if (owned.size < 2) return null;
  const ids = [...owned].sort((a, b) => a - b);

  let best: { aId: SystemId; bId: SystemId; score: number } | null = null;
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const aId = ids[i]!;
      const bId = ids[j]!;
      const score = pairScoreForGateCandidate(state, empire, behaviour, aId, bId);
      if (score <= 0) continue;
      if (best === null || score > best.score) {
        best = { aId, bId, score };
      }
    }
  }
  return best;
}

/**
 * Empire's homeworld = first owned planet's parent system. Matches
 * the convention used in `advance-ai-home-defence.ts` and
 * `pickHomeworldPosition` so the gate scoring agrees with the rest
 * of advance-ai on what "home" means.
 */
function empireHomeworldSystemId(
  state: GameState,
  empire: Empire,
): SystemId | null {
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (planet) return planet.systemId;
  }
  return null;
}
